import { ClientCombobox } from "@/components/ClientCombobox";
import { useEffect, useState, useCallback } from "react";
import { DropZone } from "@/components/DropZone";
import { useColumnResize } from "@/hooks/useColumnResize";
import { api, formatApiErrorDetail, fmtDate, openAuthPdf } from "@/lib/api";
import { InternalLayout, PageHeader } from "@/components/InternalLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { FileTextIcon as FileText, UploadSimpleIcon as UploadSimple, TrashIcon as Trash, DownloadSimpleIcon as DownloadSimple } from "@phosphor-icons/react";

const CATEGORIE = ["Contratto", "Privacy", "DDT", "Fattura", "Preventivo", "Certificato", "Altro"];

export default function Documents() {
  const [docs, setDocs] = useState(null);
  const [clients, setClients] = useState([]);
  const [fCliente, setFCliente] = useState("all");
  const [fCat, setFCat] = useState("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [upClient, setUpClient] = useState("");
  const [upCat, setUpCat] = useState("Contratto");
  const [upFile, setUpFile] = useState(null);
  const DW_DOC = { doc: 260, cliente: 180, categoria: 140, caricato: 160, data: 120, visibile: 150, azioni: 110 };
  const { colW, startResize, totalW } = useColumnResize("documents_col_w", DW_DOC);
  const RH = ({ k }) => <span onMouseDown={(e) => startResize(e, k)} onClick={(e) => e.stopPropagation()} className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40 active:bg-primary/60" />;
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => {
    const params = {};
    if (fCliente !== "all") params.cliente_id = fCliente;
    if (fCat !== "all") params.categoria = fCat;
    if (q) params.q = q;
    api.get("/documents", { params }).then(({ data }) => setDocs(data));
  }, [fCliente, fCat, q]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { api.get("/clients").then(({ data }) => setClients(data)); }, []);

  const doUpload = async () => {
    if (!upClient) { toast.error("Seleziona un cliente"); return; }
    if (!upFile) { toast.error("Seleziona un file PDF"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", upFile);
    fd.append("categoria", upCat);
    try {
      await api.post(`/clients/${upClient}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento caricato");
      setOpen(false); setUpFile(null); setUpClient(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const del = async (d) => { if (!window.confirm(`Eliminare ${d.filename}?`)) return; await api.delete(`/documents/${d.id}`); toast.success("Eliminato"); load(); };
  const toggleVis = async (d, val) => {
    setDocs((prev) => prev.map((x) => (x.id === d.id ? { ...x, visibile_cliente: val } : x)));
    try { await api.patch(`/documents/${d.id}/visibility`, { visibile_cliente: val }); toast.success(val ? "Visibile al cliente" : "Nascosto al cliente"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); load(); }
  };

  return (
    <InternalLayout>
      <div className="p-8 lg:p-12">
        <PageHeader
          title="Documenti"
          subtitle="Archivio documentale · ogni PDF è associato a un cliente"
          actions={<Button data-testid="new-document-btn" onClick={() => setOpen(true)} className="rounded-sm gap-2"><UploadSimple size={16} weight="bold" /> Carica documento</Button>}
        />

        <div className="flex flex-wrap gap-3 mb-4">
          <Input data-testid="doc-search" placeholder="Cerca nome file..." value={q} onChange={(e) => setQ(e.target.value)} className="max-w-sm rounded-sm bg-white" />
          <ClientCombobox clients={clients} value={fCliente} width="w-64" onChange={setFCliente} extraOptions={[{ value: "all", label: "Tutti i clienti" }]} placeholder="Cliente" testid="doc-filter-cliente" />
          <Select value={fCat} onValueChange={setFCat}><SelectTrigger className="w-48 rounded-sm bg-white" data-testid="doc-filter-categoria"><SelectValue placeholder="Categoria" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutte le categorie</SelectItem>{CATEGORIE.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
        </div>

        <Card className="rounded-sm border-border shadow-none">
          <div className="overflow-x-auto">
          <Table style={{ tableLayout: "fixed", width: totalW }} className="[&_td]:overflow-hidden [&_th]:overflow-hidden">
            <TableHeader><TableRow>
              <TableHead style={{ width: colW.doc }} className="relative">Documento<RH k="doc" /></TableHead><TableHead style={{ width: colW.cliente }} className="relative">Cliente<RH k="cliente" /></TableHead><TableHead style={{ width: colW.categoria }} className="relative">Categoria<RH k="categoria" /></TableHead><TableHead style={{ width: colW.caricato }} className="relative">Caricato da<RH k="caricato" /></TableHead><TableHead style={{ width: colW.data }} className="relative">Data<RH k="data" /></TableHead><TableHead style={{ width: colW.visibile }} className="relative">Visibile al cliente<RH k="visibile" /></TableHead><TableHead style={{ width: colW.azioni }} className="relative text-right">Azioni<RH k="azioni" /></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {docs?.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">Nessun documento.</TableCell></TableRow>}
              {docs?.map((d) => (
                <TableRow key={d.id} data-testid="document-row" className="hover:bg-secondary">
                  <TableCell className="font-medium flex items-center gap-2"><FileText size={16} className="text-muted-foreground" /> {d.filename}</TableCell>
                  <TableCell className="text-muted-foreground">{d.cliente_nome || "—"}</TableCell>
                  <TableCell><Badge variant="outline" className="rounded-sm">{d.categoria}</Badge></TableCell>
                  <TableCell className="text-muted-foreground text-sm">{d.uploaded_by_client ? <Badge variant="outline" className="rounded-sm text-blue-700">Caricato dal cliente</Badge> : (d.uploaded_by || "—")}</TableCell>
                  <TableCell className="tabular-nums text-muted-foreground">{fmtDate(d.uploaded_at)}</TableCell>
                  <TableCell><Switch checked={!!d.visibile_cliente} onCheckedChange={(v) => toggleVis(d, v)} data-testid="doc-visibility-switch" /></TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-primary" onClick={() => openAuthPdf(`/documents/${d.id}/download`)} title="Apri"><DownloadSimple size={16} /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => del(d)}><Trash size={16} /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        </Card>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Carica documento</DialogTitle></DialogHeader>
          <div className="py-2 space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider">Cliente *</Label>
              <div className="mt-1"><ClientCombobox clients={clients} value={upClient} onChange={setUpClient} placeholder="Seleziona cliente" testid="up-cliente" /></div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Categoria</Label>
              <Select value={upCat} onValueChange={setUpCat}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="up-categoria"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIE.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <DropZone onFiles={(fl) => setUpFile(fl?.[0] || null)} className="block">
              <Label className="text-xs uppercase tracking-wider">File PDF *</Label>
              <Input type="file" accept="application/pdf,.pdf" onChange={(e) => setUpFile(e.target.files?.[0] || null)} className="mt-1 rounded-sm" data-testid="up-file" />
            </DropZone>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Annulla</Button>
            <Button data-testid="up-submit" onClick={doUpload} disabled={uploading} className="rounded-sm">{uploading ? "Caricamento..." : "Carica"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </InternalLayout>
  );
}
